import Box from '@mui/material/Box';
import {
  CollectionOpts,
  CollectionOrderDirection,
  ResourceType,
  isLoadingStatus,
  useCollectionRoute,
  useCollectionStatus,
  useRefreshCollection,
} from 'json-api-hooks';
import React, { FC, PropsWithChildren, createContext, useEffect, useState } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';

import { retrieveParams } from '../../lib/iris';

export enum FilterType {
  Checkboxes = 'checkboxes',
  Select = 'select',
}

export interface FilterOption {
  label: string;
  value: string;
}

export interface AvailableFilter {
  attribute: string;
  label: string;
  options?: FilterOption[];
  type: FilterType;
  useOptions?: () => FilterOption[];
}

export interface AvailableSort {
  attribute: string;
  directions?: Record<CollectionOrderDirection, string>;
  label: string;
  icon: 'amount' | 'alpha' | 'numeric';
}

export interface CollectionContext<T extends ResourceType = ResourceType> {
  alterCollectionOpts: (newParams: Partial<CollectionOpts<T>>) => CollectionOpts<T> | undefined;
  availableFilters?: AvailableFilter[];
  availableSorts?: AvailableSort[];
  collectionOpts: CollectionOpts<T>;
  refreshCollection: () => void;
  resourceType: T;
  updateCollection: (newParams: Partial<CollectionOpts<T>>) => void;
}

export const CollectionContext = createContext<CollectionContext>({} as CollectionContext);

export interface CollectionProps<T extends ResourceType> extends PropsWithChildren {
  availableFilters?: AvailableFilter[];
  availableSorts?: AvailableSort[];
  fresh?: boolean;
  fullPage?: boolean;
  onChange?: (newOpts: CollectionOpts<T>) => void;
  opts?: CollectionOpts<T>;
  resourceType: T;
  wrapper?: FC;
  wrapperProps?: any;
}

const OPACITY = 0.5;

const Collection = <T extends ResourceType>({
  availableFilters,
  availableSorts,
  children,
  fresh,
  navigate,
  onChange,
  opts,
  resourceType,
  wrapper: Wrapper = Box,
  wrapperProps,
}: CollectionProps<T> & { navigate?: NavigateFunction }) => {
  const refreshCollection = useRefreshCollection();

  const [requestedCollectionOpts, setRequestedCollectionOpts] = useState(opts ?? {});
  const requestedCollectionStatus = useCollectionStatus(resourceType, requestedCollectionOpts, { fetch: true });
  const [collectionOpts, setCollectionOpts] = useState(opts ?? {});
  const stringifiedOpts = JSON.stringify(opts ?? {});
  const stringifiedCollectionOpts = JSON.stringify(collectionOpts ?? {});
  const stringifiedRequestedOpts = JSON.stringify(requestedCollectionOpts);

  useEffect(() => {
    if (fresh !== false) {
      refreshCollection(resourceType, requestedCollectionOpts);
    }
  }, [fresh, stringifiedRequestedOpts]);
  useEffect(() => {
    setCollectionOpts(opts ?? {});
    setRequestedCollectionOpts(opts ?? {});
  }, [stringifiedOpts]);

  const loading = stringifiedCollectionOpts !== stringifiedRequestedOpts;
  const route = useCollectionRoute(resourceType, requestedCollectionOpts);
  useEffect(() => {
    if (loading && requestedCollectionOpts && !isLoadingStatus(requestedCollectionStatus)) {
      setCollectionOpts(requestedCollectionOpts);
      if (onChange) {
        onChange(requestedCollectionOpts);
      }
      if (navigate) {
        navigate(retrieveParams(route));
      }
    }
  }, [stringifiedRequestedOpts, requestedCollectionStatus]);

  const alterCollectionOpts = (newParams: Partial<CollectionOpts<T>>) => {
    return {
      ...collectionOpts,
      page: 1,
      ...newParams,
    };
  };

  const updateCollection = (newParams: Partial<CollectionOpts<T>>) => {
    setRequestedCollectionOpts(alterCollectionOpts(newParams));
  };

  const opacityProps = Wrapper === Box ? { opacity: loading ? OPACITY : undefined } : {};

  return (
    <CollectionContext.Provider
      value={
        {
          alterCollectionOpts,
          availableFilters,
          availableSorts,
          collectionOpts,
          refreshCollection: () => refreshCollection(resourceType, collectionOpts),
          resourceType,
          updateCollection,
        } as CollectionContext
      }
    >
      <Wrapper {...(opacityProps as any)} {...wrapperProps}>
        {children}
      </Wrapper>
    </CollectionContext.Provider>
  );
};

const CollectionFullPage = <T extends ResourceType>(props: CollectionProps<T>) => {
  const navigate = useNavigate();

  return <Collection<T> {...props} navigate={navigate} />;
};

const CollectionWrapper = <T extends ResourceType>(props: CollectionProps<T>) => {
  if (props.fullPage) {
    return <CollectionFullPage<T> {...props} />;
  }

  return <Collection<T> {...props} />;
};

export default CollectionWrapper;
